import PropTypes from 'prop-types';
import React, { Fragment, memo, useContext, useLayoutEffect, useMemo } from 'react';
import _ from 'underscore';
import { InlineInfoAlert } from '@bingads-webui-cc-react/alert';
import { LineByLineFormGroup } from '@bingads-webui-cc-react/layout';
import { PaymentContext } from '@bingads-webui-cc-react/payment-context';
import { ElementHelper } from '@bingads-webui-cc-react/helper';

import { PropTypeI18n } from './shapes';
import { StyledToggle } from './styled-toggle';

const AutoRechargeFormUnwrapped = ({
  piType,
  paymentAmount,
  i18n,
  currency,
  isAutoRechargeOn,
  setIsAutoRechargeOn,
}) => {
  const {
    paymentContext: {
      PaymentInstrumentConfigs,
    },
  } = useContext(PaymentContext);

  const supportsRecurringPaymentMapping = useMemo(
    () => _.reduce(
      PaymentInstrumentConfigs,
      (dict, { id, supportsRecurringPayments }) => ({
        ...dict,
        [id]: supportsRecurringPayments,
      }),
      {}
    ),
    [PaymentInstrumentConfigs]
  );

  const autoRechargeEnabled = useMemo(
    () => piType !== null && supportsRecurringPaymentMapping[piType],
    [piType, supportsRecurringPaymentMapping]
  );

  useLayoutEffect(
    () => {
      // if auto recharge is not enabled, reset it to undefined
      if (!autoRechargeEnabled) {
        setIsAutoRechargeOn(undefined);
      }
    },
    [autoRechargeEnabled, setIsAutoRechargeOn]
  );

  if (autoRechargeEnabled) {
    return (
      <Fragment>
        <div className="auto-recharge-form">
          <LineByLineFormGroup
            helpPopupTopic="pop_BA_Accounts_SetUpAutoRecharge"
            label={i18n.getString(_TL_('Set up auto-recharge'))}
          >
            {
              _.isNumber(paymentAmount) && ElementHelper.renderTextWithBolds(
                i18n.getString(_TL_('Auto-recharge for {0} when your balance runs low?')),
                [i18n.formatCurrency(paymentAmount, currency, { maximumFractionDigits: 2 })]
              )
            }
            <StyledToggle
              isChecked={isAutoRechargeOn}
              setIsChecked={setIsAutoRechargeOn}
              i18n={i18n}
              className="auto-recharge-toggle toggle"
            />
            { isAutoRechargeOn &&
              <InlineInfoAlert>
                {i18n.getString(_TL_('We will automatically add funds to your balance once your balance falls below a certain threshold. This way your account balance will automatically top-up to the amount set by you in advance, so your account does not hit zero and your campaigns will continue without interruption. You can turn-off this feature anytime under your Billing settings.'))}
              </InlineInfoAlert>
            }
          </LineByLineFormGroup>
        </div>
      </Fragment>
    );
  }

  return null;
};

AutoRechargeFormUnwrapped.propTypes = {
  piType: PropTypes.number,
  paymentAmount: PropTypes.number,
  i18n: PropTypeI18n.isRequired,
  currency: PropTypes.string.isRequired,
  isAutoRechargeOn: PropTypes.bool.isRequired,
  setIsAutoRechargeOn: PropTypes.func.isRequired,
  permissions: PropTypes.shape({}),
};

AutoRechargeFormUnwrapped.defaultProps = {
  paymentAmount: null,
  piType: null,
  permissions: {},
};

AutoRechargeFormUnwrapped.displayName = 'AutoRechargeForm';

export const AutoRechargeForm = memo(AutoRechargeFormUnwrapped);
